import type { GameBoard, GameLetter } from '../types'

type Vec2 = readonly [number, number]

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'

/** RNG seedável p/ reprodutibilidade */
function mulberry32(seed: number) {
  return function () {
    let t = (seed += 0x6d2b79f5)
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

function shuffled<T>(arr: T[], rnd: () => number): T[] {
  const a = arr.slice()
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

function randomLetter(rnd: () => number): string {
  return LETTERS[Math.floor(rnd() * LETTERS.length)]
}

function boundsOk(rows: number, cols: number, r: number, c: number) {
  return r >= 0 && r < rows && c >= 0 && c < cols
}

function canPlace(
  board: GameLetter[][],
  word: string,
  startR: number,
  startC: number,
  dr: number,
  dc: number
): boolean {
  const rows = board.length
  const cols = board[0].length
  const L = word.length

  // pré-checagem de limites
  const endR = startR + (L - 1) * dr
  const endC = startC + (L - 1) * dc
  if (!boundsOk(rows, cols, endR, endC)) return false

  for (let i = 0; i < L; i++) {
    const r = startR + i * dr
    const c = startC + i * dc
    const cell = board[r][c]
    if (!cell) return false
    // conflito: letra diferente
    if (cell.word.length > 0 && cell.letter !== word[i]) return false
  }
  return true
}

function place(
  board: GameLetter[][],
  word: string,
  startR: number,
  startC: number,
  dr: number,
  dc: number
): { written: Array<[number, number]> } {
  const written: Array<[number, number]> = []
  for (let i = 0; i < word.length; i++) {
    const r = startR + i * dr
    const c = startC + i * dc
    const cell = board[r][c]
    if (cell.word.length === 0) {
      // escrita nova (memoriza p/ desfazer em backtrack)
      cell.letter = word[i]
      written.push([r, c])
    }
    if (!cell.word.includes(word)) cell.word.push(word)
  }
  return { written }
}

function unplace(
  board: GameLetter[][],
  word: string,
  written: Array<[number, number]>
) {
  // remove "marca" da palavra das células e limpa letras onde necessário
  for (const [r, c] of written) {
    const cell = board[r][c]
    // remove esta palavra da lista
    const idx = cell.word.indexOf(word)
    if (idx >= 0) cell.word.splice(idx, 1)
    // se ficou sem palavras ancoradas, volta a ser “vazio” (usa placeholder)
    if (cell.word.length === 0) {
      cell.letter = '?' // placeholder até preencher ruído
    }
  }
}

/** Heurística: preferir starts que cruzem letras existentes */
function scoreStart(
  board: GameLetter[][],
  word: string,
  r: number,
  c: number,
  dr: number,
  dc: number
): number {
  let hits = 0
  for (let i = 0; i < word.length; i++) {
    const rr = r + i * dr
    const cc = c + i * dc
    const cell = board[rr][cc]
    if (cell.word.length > 0 && cell.letter === word[i]) hits++
  }
  return hits
}

function makeEmptyBoard(rows: number, cols: number): GameLetter[][] {
  return Array.from({ length: rows }, (_r, r) =>
    Array.from({ length: cols }, (_c, c) => ({
      letter: '?',
      word: [],
      row: r,
      column: c,
      isSelected: false
    }))
  )
}

function byHardness(a: string, b: string): number {
  // tamanho desc; em empate, diversidade de letras desc
  const dl = b.length - a.length
  if (dl !== 0) return dl
  const uniq = (s: string) => new Set(s).size
  return uniq(b) - uniq(a)
}

function directionsFromConfig(cfg: {
  vertical: boolean
  horizontal: boolean
  diagLeft: boolean
  diagRight: boolean
}): Vec2[] {
  const dirs: Vec2[] = []
  if (cfg.horizontal) dirs.push([0, 1]) // →
  if (cfg.vertical) dirs.push([1, 0]) // ↓
  if (cfg.diagRight) dirs.push([1, 1]) // ↘
  if (cfg.diagLeft) dirs.push([1, -1]) // ↙
  return dirs
}

/** Gerador robusto por backtracking (forward only) */
export function generateBoardStrict({
  boardSize,
  words,
  allowDiagLeft,
  allowDiagRight,
  seed = 12345
}: {
  boardSize: number
  words: string[]
  allowDiagLeft: boolean
  allowDiagRight: boolean
  seed?: number
}): GameBoard {
  const rnd = mulberry32(seed)
  const rows = boardSize
  const cols = boardSize

  const board = makeEmptyBoard(rows, cols)

  const dirs = directionsFromConfig({
    vertical: true,
    horizontal: true,
    diagLeft: allowDiagLeft,
    diagRight: allowDiagRight
  })

  const sorted = words.map(w => w.toUpperCase().trim()).sort(byHardness)

  // backtracking
  function placeWordAt(index: number): boolean {
    if (index >= sorted.length) return true

    const w = sorted[index]

    // candidatos de (r,c,dir), embaralhados e ordenados por “cruzamento”
    const starts: Array<[number, number, Vec2, number]> = []

    // gera todos os starts válidos p/ cada direção
    for (const [dr, dc] of dirs) {
      // limita r/c inicial já respeitando o comprimento
      const rMin = 0,
        cMin = 0
      const rMax = dr === 0 ? rows - 1 : rows - w.length
      const cMax = dc === 0 ? cols - 1 : dc > 0 ? cols - w.length : cols - 1

      const rStartMin = rMin
      const rStartMax = rMax
      const cStartMin = dc >= 0 ? cMin : w.length - 1 // ↙ precisa começar mais à direita
      const cStartMax = cMax

      for (let r = rStartMin; r <= rStartMax; r++) {
        for (let c = cStartMin; c <= cStartMax; c++) {
          if (!canPlace(board, w, r, c, dr, dc)) continue
          const sc = scoreStart(board, w, r, c, dr, dc)
          starts.push([r, c, [dr, dc], sc])
        }
      }
    }

    // embaralha para variedade e, depois, ordena por melhor score (mais cruzamentos)
    const shuffledStarts = shuffled(starts, rnd).sort((a, b) => b[3] - a[3])

    for (const [r, c, [dr, dc]] of shuffledStarts) {
      const { written } = place(board, w, r, c, dr, dc)
      if (placeWordAt(index + 1)) return true
      unplace(board, w, written)
    }

    return false
  }

  const ok = placeWordAt(0)
  if (!ok) {
    throw new Error(
      'Impossível alocar todas as palavras com as restrições atuais.'
    )
  }

  // preencher ruído
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const cell = board[r][c]
      if (cell.letter === '?') cell.letter = randomLetter(rnd)
    }
  }

  return {
    board,
    words: sorted, // ou as originais na ordem original, se preferir
    rows,
    columns: cols
  }
}
